import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/components/theme-provider';
import { Toaster } from '@/components/ui/sonner';

// Pages
import HomePage from '@/pages/HomePage';
import AuthPage from '@/pages/AuthPage';
import PricingPage from '@/pages/PricingPage';
import SubscribePage from '@/pages/SubscribePage';
import SuccessPage from '@/pages/SuccessPage';
import DemoPage from '@/pages/DemoPage';
import ChangeLogsPage from '@/pages/ChangeLogsPage';
import InstallationPage from '@/pages/InstallationPage';
import InstallTestPage from '@/pages/InstallTestPage';
import AccountPage from '@/pages/AccountPage';
import NotFoundPage from '@/pages/NotFoundPage';
import VoiceCommandDebugger from '@/pages/VoiceCommandDebugger';
import DeveloperDocsPage from '@/pages/DeveloperDocsPage';

// Admin pages
import AdminLayout from '@/components/AdminLayout';
import AdminUsersPage from '@/pages/AdminUsersPage';
import AdminWidgetStatusPage from '@/pages/AdminWidgetStatusPage';
import AdminPermanentPlansPage from '@/pages/AdminPermanentPlansPage';
import AdminDocumentationPage from '@/pages/AdminDocumentationPage';

// Components
import ProtectedRoute from '@/components/ProtectedRoute';
import { AuthProvider } from '@/providers/AuthProvider';

import './App.css';

// Initialize Query Client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <ThemeProvider defaultTheme="dark" storageKey="accesstopia-theme">
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <div className="app-container">
              {isMounted && (
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/auth" element={<AuthPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/subscribe" element={<SubscribePage />} />
                  <Route path="/success" element={<SuccessPage />} />
                  <Route path="/demo" element={<DemoPage />} />
                  <Route path="/changelogs" element={<ChangeLogsPage />} />
                  <Route path="/installation" element={<InstallationPage />} />
                  <Route path="/installation/test" element={<InstallTestPage />} />
                  <Route path="/docs" element={<DeveloperDocsPage />} />
                  <Route path="/docs/:section" element={<DeveloperDocsPage />} />
                  
                  {/* Protected routes */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/account" element={<AccountPage />} />
                    <Route path="/voice-debugger" element={<VoiceCommandDebugger />} />
                  </Route>
                  
                  {/* Admin routes */}
                  <Route path="/admin" element={
                    <ProtectedRoute adminOnly>
                      <AdminLayout />
                    </ProtectedRoute>
                  }>
                    <Route index element={<AdminUsersPage />} />
                    <Route path="users" element={<AdminUsersPage />} />
                    <Route path="widget-status" element={<AdminWidgetStatusPage />} />
                    <Route path="permanent-plans" element={<AdminPermanentPlansPage />} />
                    <Route path="documentation" element={<AdminDocumentationPage />} />
                  </Route>
                  
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              )}
              <Toaster />
            </div>
          </AuthProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
